
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { Memory } from '@/types/memory';
import { Share, Copy, Mail, MessageSquare, Image } from 'lucide-react';
import { format, isValid } from 'date-fns';
import { logger } from '@/utils/logger';
import QuoteCardDialog from '@/components/quotes/QuoteCardDialog';

interface ShareMemoryProps {
  memory: Memory;
}

const ShareMemory: React.FC<ShareMemoryProps> = ({ memory }) => {
  const [email, setEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const [showCardDialog, setShowCardDialog] = useState(false);

  // Function to safely format date
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (!isValid(date)) {
        return 'Unknown date';
      }
      return format(date, 'MMMM d, yyyy');
    } catch (error) {
      logger.error('Error formatting date:', error);
      return 'Unknown date';
    }
  };

  const handleCopyToClipboard = () => {
    const formattedDate = formatDate(memory.date);
    const text = `"${memory.content}" - ${memory.childName}, ${formattedDate}`;
    
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleEmailShare = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter an email address.",
        variant: "destructive"
      });
      return;
    }

    const formattedDate = formatDate(memory.date);
    const subject = encodeURIComponent(`Memory from ${memory.childName}`);
    const body = encodeURIComponent(`"${memory.content}"\n\n- ${memory.childName}, ${formattedDate}`);

    // Open default email client with pre-filled content
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;

    setEmail('');
  };

  const handleSMSShare = () => {
    // In a real app, this would open the SMS app
    const formattedDate = formatDate(memory.date);
    const text = `"${memory.content}" - ${memory.childName}, ${formattedDate}`;
    
    // Check if the Web Share API is available (mainly on mobile)
    if (navigator.share) {
      navigator.share({
        title: `Memory from ${memory.childName}`,
        text: text
      }).catch(error => logger.log('Error sharing', error));
    } else {
      // Fallback for desktop
      toast({
        title: "SMS sharing",
        description: "SMS sharing is available on mobile devices."
      });
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Share className="h-4 w-4 mr-2" />
          Share
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share this memory</DialogTitle>
          <DialogDescription>
            Choose how you'd like to share this special moment.
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="sms" className="mt-4">
          <TabsList className="grid grid-cols-4">
            <TabsTrigger value="sms">SMS</TabsTrigger>
            <TabsTrigger value="email">Email</TabsTrigger>
            <TabsTrigger value="copy">Copy</TabsTrigger>
            <TabsTrigger value="image">Image</TabsTrigger>
          </TabsList>
          
          <TabsContent value="sms" className="mt-4">
            <div className="flex flex-col space-y-4">
              <p className="text-sm">
                Share this memory via text message. On mobile devices, this will open your messaging app.
              </p>
              <Button onClick={handleSMSShare}>
                <MessageSquare className="h-4 w-4 mr-2" />
                Share via SMS
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="email" className="mt-4">
            <form onSubmit={handleEmailShare}>
              <div className="flex flex-col space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input 
                    id="email" 
                    type="email" 
                    placeholder="friend@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit">
                  <Mail className="h-4 w-4 mr-2" />
                  Send Email
                </Button>
              </div>
            </form>
          </TabsContent>
          
          <TabsContent value="copy" className="mt-4">
            <div className="flex flex-col space-y-4">
              <div className="bg-muted p-3 rounded-md">
                <p className="italic mb-1">"{memory.content}"</p>
                <p className="text-sm">- {memory.childName}, {formatDate(memory.date)}</p>
              </div>
              <Button onClick={handleCopyToClipboard}>
                <Copy className="h-4 w-4 mr-2" />
                {copied ? "Copied!" : "Copy to Clipboard"}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="image" className="mt-4">
            <div className="flex flex-col space-y-4">
              <p className="text-sm">
                Create a beautiful quote card image to share on social media.
              </p>
              <Button onClick={() => setShowCardDialog(true)}>
                <Image className="h-4 w-4 mr-2" />
                Create Quote Card
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        <QuoteCardDialog
          open={showCardDialog}
          onOpenChange={setShowCardDialog}
          data={{
            content: memory.content,
            childName: memory.childName,
            date: memory.date,
            context: memory.context,
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ShareMemory;
